/**
 * The one interface pages consume.
 *
 * Pages never call `fetch` and never import a provider: they ask TanStack Query
 * for a `ConsoleApi` method. Swapping fixture data for the live façade is one
 * assignment in `provider.ts` — no page changes (plan §3 / I4).
 *
 * Method list == the contract operations that exist today in
 * `openapi/openapi.yaml` (the v1.1 additive `/ops/v1` surface: activations,
 * alarms + ack, OLT inventory / ONU table / direct authorize, device optical,
 * `/integration/v1` diagnostics, the fleet job list `GET /ops/v1/jobs` and the five
 * bulk/workflow doors of `routers/operations.py` — all wired since U-16-f), plus
 * exactly ONE console aggregate the HTTP provider still refuses:
 * `getDeviceCapabilities`. No door publishes a device's capability list —
 * `grep -n capabilities acs_service/routers/*.py` finds only the permission matrix in
 * `identity.py`, the VENDOR registry in `capabilities.py` (`/vendor-registry`,
 * `/devices/{id}/identity`), and the `device.get("capabilities")` READ inside
 * `diagnostics.py:44`, and `openapi.yaml` has no such path — so that method answers
 * `EndpointPendingError` rather than inventing a route.
 */

import { ApiError } from './errors';
import type {
  ActionRequest,
  ActionResponse,
  ActivationAccepted,
  CloudEvent,
  ActivationRequest,
  ActivationStatus,
  AlarmAckRequest,
  AlarmAckResponse,
  AlarmAssignRequest,
  AlarmAssignResponse,
  AlarmClearRequest,
  AlarmClearResponse,
  AlarmEscalateRequest,
  AlarmEscalateResponse,
  AlarmsResponse,
  AlarmStats,
  AuditListResponse,
  PresetWriteResponse,
  ReconciliationListResponse,
  TemplateListResponse,
  BindingRequest,
  BindingResponse,
  Campaign,
  CampaignAccepted,
  CampaignCreateRequest,
  CampaignHalted,
  CampaignListResponse,
  CampaignRolledBack,
  CampaignRollbackRequest,
  CampaignScheduled,
  DeletedResponse,
  DeviceCapabilities,
  DeviceStats,
  DeviceSummary,
  DevicesListResponse,
  DiagnosticsRequest,
  EffectivePermissions,
  EventReadResponse,
  FirmwareListResponse,
  FirmwareUploadAccepted,
  Health,
  Job,
  JobsListResponse,
  ListJobsQuery,
  ListOperationsQuery,
  OperationsResponse,
  OperationRunAccepted,
  OperationRunRequest,
  OperationValueResponse,
  OperationWriteAccepted,
  OperationWriteRequest,
  RawWriteRequest,
  ListAlarmsQuery,
  ListAuditQuery,
  ListCampaignsQuery,
  ListDevicesQuery,
  ListEventsQuery,
  ListFirmwareQuery,
  ListOltOnusQuery,
  ListParamsQuery,
  ListStationsQuery,
  MatrixMutationAccepted,
  MatrixWriteBody,
  OnuAuthorizeRequest,
  PermissionMatrixRead,
  OnuAuthorizeResponse,
  OltListResponse,
  OnuTableResponse,
  OpticalDegrading,
  OpticalDegradingWindow,
  OpticalHistory,
  OpticalWorst,
  OpticalRead,
  OpticalWindow,
  ParamsResponse,
  OpsSettingsRead,
  ProfileActivationAccepted,
  ProfileActivationRequest,
  ProfileDocument,
  ProfileReadResponse,
  ProfileReadinessResponse,
  ProfileRollbackRequest,
  ProfileValidationReport,
  ProfileVersionListResponse,
  SessionGranted,
  SessionRequest,
  TotpSessionRequest,
  SessionRevoked,
  ServiceProfile,
  ServiceProfileListResponse,
  ServiceProfileWriteRequest,
  SigningKeyListResponse,
  StationsResponse,
  Subscriber,
  SubscriberCreateRequest,
  SubscriberListResponse,
  SubscriberOverview,
  SubscriberSearchRequest,
  SubscriberSearchResponse,
  SystemComponentsResponse,
  SystemQueuesResponse,
  SystemWorkersResponse,
  UserCreateRequest,
  UserListResponse,
  UserMutationAccepted,
  UserPatchRequest,
  ListServiceProfilesQuery,
  ListSubscribersQuery,
  ListMaintenanceQuery,
  MaintenanceCancelResponse,
  MaintenanceCreateRequest,
  Incident,
  IncidentAttachRequest,
  IncidentAttachResponse,
  IncidentCloseRequest,
  IncidentCorrelateResponse,
  IncidentCreateRequest,
  IncidentListResponse,
  IncidentNoteRequest,
  ListIncidentCorrelateQuery,
  ListIncidentsQuery,
  ListNotificationDeliveriesQuery,
  NotificationChannel,
  NotificationChannelCreateRequest,
  NotificationChannelDeletedResponse,
  NotificationChannelListResponse,
  NotificationChannelUpdateRequest,
  NotificationDelivery,
  NotificationDeliveryListResponse,
  MaintenanceCreateResponse,
  MaintenanceListResponse,
  PonCapacityResponse,
} from './types';

/** Writes carry an idempotency key so a double-click cannot double-fire. */
/**
 * DA-63: the cancellation a query hook forwards from `QueryFunctionContext`. Reads only —
 * a mutation has no react-query-provided signal to forward, and a write the user asked for
 * should not be cancelled by a re-render anyway.
 */
export interface ReadOptions {
  signal?: AbortSignal;
}

export interface WriteOptions {
  idempotencyKey: string;
}

/* ---- the live-event channel (N9 slice 2 / G-UI-01) ----
   `GET /integration/v1/events/stream` is not a request/response read: it is a
   long-lived SSE body whose frames arrive over time, so it is a HANDLE, not a
   Promise. The bearer cannot travel on a browser `EventSource` (no Authorization
   header), which is why the channel exists as a provider method at all — pages
   still never touch `fetch`. */

/** One event off the stream: the door's `id:` line plus the CloudEvent it carried. */
export interface LiveEventFrame {
  /** The door's own opaque cursor token (`k2:` …) — passed through, never interpreted. */
  cursorId: string | null;
  event: CloudEvent;
}

/** Mirrors `SseStreamStatus` of `api/sse.ts` without importing it into every consumer. */
export type LiveStreamStatus = 'connecting' | 'live' | 'reconnecting' | 'stopped';

export interface LiveEventHandler {
  onEvent: (frame: LiveEventFrame) => void;
  onStatus: (status: LiveStreamStatus) => void;
}

export interface EventStreamOptions {
  /** Cancels the whole channel — reconnect loop included. */
  signal: AbortSignal;
  /** Fired once when the stream door answers 401 to this console's bearer. */
  onUnauthorized?: (status: number) => void;
}

export interface EventStreamHandle {
  close(): void;
}

/**
 * The raw-image door's input (E1 / G-FW-01): the metadata travels as query parameters and
 * `body` is the image itself, sent as `application/octet-stream` — not base64 inside a
 * document, which is what makes a 20-100 MB ONT image registerable at all.
 *
 * `checksum_sha256` is the vendor's published claim about the file. The console must NOT
 * compute it from `body`: a digest of the bytes being sent can only agree with them, and
 * the whole point of E2 is to catch the case where the file the operator grabbed is not
 * the file the vendor shipped.
 */
export interface FirmwareImageUpload {
  brand: string;
  model: string;
  version: string;
  checksum_sha256: string;
  /** Optional second claim; when present the service requires it to equal the body length. */
  size_bytes?: number;
  body: Blob | Uint8Array;
}

/**
 * The contract makes `Idempotency-Key` optional on the v1 write surface; the
 * console policy is to always send one. An empty key therefore means a bug in
 * the calling code, not a user choice — fail as a contract error instead of
 * firing a non-replayable write.
 */
export function assertWriteKey(key: string): string {
  if (typeof key !== 'string' || key.trim().length === 0) {
    throw new ApiError('contract', 'write attempted without an Idempotency-Key');
  }
  return key;
}

export interface ConsoleApi {
  /** Where the data comes from — surfaced in the header so nobody is fooled. */
  readonly dataSource: 'fixture' | 'http';

  /* ---- read surface (contract operations) ---- */
  health(options?: ReadOptions): Promise<Health>;
  listDevices(query: ListDevicesQuery, options?: ReadOptions): Promise<DevicesListResponse>;
  getDevice(acsDeviceId: string, options?: ReadOptions): Promise<DeviceSummary>;
  /**
   * `GET /ops/v1/stats/devices` — the fleet's counts over the whole store (V4 / G-TLM-05).
   *
   * This is the door the dashboard's headline cards read: `listDevices` answers a PAGE, and a
   * page reduced into «fleet health» is the wrong-number bug the door was built to end.
   */
  getDeviceStats(options?: ReadOptions): Promise<DeviceStats>;
  /** `GET /ops/v1/stats/alarms` — the lifecycle counts the dashboard shows (V4 / G-TLM-05). */
  getAlarmStats(options?: ReadOptions): Promise<AlarmStats>;
  getParameters(
    acsDeviceId: string,
    query: ListParamsQuery,
    options?: ReadOptions,
  ): Promise<ParamsResponse>;
  getStations(
    acsDeviceId: string,
    query: ListStationsQuery,
    options?: ReadOptions,
  ): Promise<StationsResponse>;
  getJob(jobId: string): Promise<Job>;
  pollEvents(query: ListEventsQuery, options?: ReadOptions): Promise<EventReadResponse>;
  /** `GET /integration/v1/events/stream` — the SSE channel (N9 slice 2). A handle, not a Promise. */
  streamEvents(handler: LiveEventHandler, options: EventStreamOptions): EventStreamHandle;

  /* ---- write surface (Idempotency-Key required by the plan; optional in the
        contract, always sent here) ---- */
  runAction(
    acsDeviceId: string,
    body: ActionRequest,
    options: WriteOptions,
  ): Promise<ActionResponse>;
  bindDevice(body: BindingRequest, options: WriteOptions): Promise<BindingResponse>;

  /* ---- ZTP activations — root-absolute `/ops/v1` paths (v1.1 additive) ----
     `POST /ops/v1/activations` (scope activation:write, Idempotency-Key
     REQUIRED) and `GET /ops/v1/activations/{activation_id}` (activation:read).
     No device/online gate: the ONU is by definition not registered yet. */
  createActivation(body: ActivationRequest, options: WriteOptions): Promise<ActivationAccepted>;
  getActivation(activationId: string, options?: ReadOptions): Promise<ActivationStatus>;

  /* ---- device diagnostics — `/integration/v1` (v1.1 additive, scope
        diagnostics:run). Unknown device 404, offline 409 device-offline,
        speedtest without the tr143 capability 422 action-unsupported. */
  runDiagnostics(
    acsDeviceId: string,
    body: DiagnosticsRequest,
    options: WriteOptions,
  ): Promise<ActionResponse>;
  getDiagnostics(acsDeviceId: string, jobId: string, options?: ReadOptions): Promise<Job>;

  /* ---- alarms feed — `/ops/v1/alarms` (v1.1 additive, scope alarm:read) ----
     Rows are MATERIALIZED server-side, not derived at read time: the server
     writes each alarm into its own `acs_alarms` collection at raise time
     (the store's raise hook inside the outbox enqueue path), and the feed
     reads that collection with a bounded page — never the outbox behind it.
     `alarm_id` is the event id. An unknown `severity` is a 422
     `reference-invalid`, and acking an id that is not an alarm-bearing event
     is a 404 `alarm-not-found`. */
  listAlarms(query: ListAlarmsQuery, options?: ReadOptions): Promise<AlarmsResponse>;

  /**
   * `POST /ops/v1/alarms/{event_id}:ack` (scope `ops:write`). The contract's own
   * dedupe is the `alarm.ack` audit ledger keyed by (alarm, client), so the
   * header is NOT required here — but the console policy is to always send one
   * (`assertWriteKey`), and the page derives it from the alarm id, so re-acking
   * the same alarm never mints a second key while the server still answers
   * `already_acked` instead of writing a second entry.
   */
  ackAlarm(
    eventId: string,
    body: AlarmAckRequest,
    options: WriteOptions,
  ): Promise<AlarmAckResponse>;

  /* ---- the lifecycle doors (N2) — `:clear`, `:escalate`, `:assign` ----
     Capability `alarm.ack` (edit) and a HUMAN caller only: the server
     refuses a machine assertion with 403 `actor-not-human`, because these
     doors record a named person's responsibility (G-ALM-06). The server's
     replay guard is the row's own state — the same person closing twice
     answers `already_cleared: true` and writes nothing, and another
     operator's closure is refused with 409 `alarm-already-cleared`. */

  clearAlarm(
    eventId: string,
    body: AlarmClearRequest,
    options: WriteOptions,
  ): Promise<AlarmClearResponse>;

  escalateAlarm(
    eventId: string,
    body: AlarmEscalateRequest,
    options: WriteOptions,
  ): Promise<AlarmEscalateResponse>;

  assignAlarm(
    eventId: string,
    body: AlarmAssignRequest,
    options: WriteOptions,
  ): Promise<AlarmAssignResponse>;

  /* ---- maintenance windows (ALM-05) — `/ops/v1/maintenance` ----
     A planned quiet night: while a window covers an event — by the EVENT's
     own time — the raise path writes no alarm row for it, counts the noise
     on the window's `suppressed` counter, and the outbox keeps the event.
     The list door is `alarm.read` (OFFSET-paged, newest first); the create
     and cancel doors are `alarm.ack` at edit and HUMAN-ONLY — the server
     refuses a machine assertion with 403 `actor-not-human`, because a plan
     is a named person's responsibility. Cancel is CAS: the loser of a race
     hears `already_cancelled` with the winner's name. The list door reads
     `state` (the row's two-word alphabet: `active`/`cancelled` — no
     `expired`, a window whose end passed is still `active` and simply never
     matches again), `offset` and `limit` (1..200). */

  listMaintenance(
    query: ListMaintenanceQuery,
    options?: ReadOptions,
  ): Promise<MaintenanceListResponse>;

  /** `window` is one ISO-8601 UTC interval `start/end` — the same shape the
   *  campaign door reads. A device `target` must name a known device (404
   *  `device-not-found`); an OLT target is free text the façade cannot check. */
  createMaintenance(
    body: MaintenanceCreateRequest,
    options: WriteOptions,
  ): Promise<MaintenanceCreateResponse>;

  cancelMaintenance(windowId: string, options: WriteOptions): Promise<MaintenanceCancelResponse>;

  /* ---- incidents (N5) — `/ops/v1/incidents` ----
     The operator's row about one outage: the alarms it links are evidence it
     cites, not rows it owns — closing it closes nothing else. The correlate
     and the two GETs answer to `alarm.read` (§۱'s contraction rule: reading
     an incident is reading alarms); the four writes are `incident.manage` at
     edit, HUMAN-ONLY. Create and note require an Idempotency-Key (a replay of
     a note would be a SECOND timeline entry); attach de-duplicates on the id
     set and close is state-guarded, so their natural replay is equivalent
     without one. */

  correlateIncidents(
    query: ListIncidentCorrelateQuery & { offset?: number },
    options?: ReadOptions,
  ): Promise<IncidentCorrelateResponse>;

  listIncidents(query: ListIncidentsQuery, options?: ReadOptions): Promise<IncidentListResponse>;

  getIncident(incidentId: string, options?: ReadOptions): Promise<Incident>;

  createIncident(body: IncidentCreateRequest, options: WriteOptions): Promise<Incident>;

  noteIncident(
    incidentId: string,
    body: IncidentNoteRequest,
    options: WriteOptions,
  ): Promise<Incident>;

  attachIncident(
    incidentId: string,
    body: IncidentAttachRequest,
    options: WriteOptions,
  ): Promise<IncidentAttachResponse>;

  closeIncident(
    incidentId: string,
    body: IncidentCloseRequest,
    options: WriteOptions,
  ): Promise<Incident>;

  /* ---- notification channels (N6) — `/ops/v1/notification-channels` ----
     The alarm's destination: a webhook-channel registry with per-severity
     routing. ALL six doors answer to ONE key — `notify.manage`, reads
     INCLUDED (plan D8: a channel URL may carry a token in its query, so a
     viewer that must not write does not read either). Every write takes an
     Idempotency-Key; `:test` mints a REAL pending intent the notify worker
     delivers through the same path every alarm takes, and its key IS the
     intent's dedupe key. The secret is write-only: the row answers
     `has_secret` and never echoes it. */

  listNotificationChannels(options?: ReadOptions): Promise<NotificationChannelListResponse>;

  createNotificationChannel(
    body: NotificationChannelCreateRequest,
    options: WriteOptions,
  ): Promise<NotificationChannel>;

  updateNotificationChannel(
    channelId: string,
    body: NotificationChannelUpdateRequest,
    options: WriteOptions,
  ): Promise<NotificationChannel>;

  deleteNotificationChannel(
    channelId: string,
    options: WriteOptions,
  ): Promise<NotificationChannelDeletedResponse>;

  testNotificationChannel(channelId: string, options: WriteOptions): Promise<NotificationDelivery>;

  listNotificationDeliveries(
    query: ListNotificationDeliveriesQuery,
    options?: ReadOptions,
  ): Promise<NotificationDeliveryListResponse>;

  /* ---- OLT views + optical — `/ops/v1` (v1.1 additive, scope ops:read) ----
     Proxied from the standalone OLT adapter: an unknown OLT is 404
     `olt-not-found` and an unreachable adapter 502 `adapter-unreachable`. */
  /* ---- the operations audit ledger — `GET /ops/v1/audit` (R7, U-16-d) ----
     Capability `fleet.audit.read` at `read`; a machine caller needs the frozen `audit:read` scope,
     NOT `ops:read`: `acs_service/routers/audit.py` declares the ledger the compliance surface, so the
     party that renders ops dashboards does not inherit the trail. Read-only ⇒ no `Idempotency-Key`
     (the ruled rule is replay-equivalence, and a GET is replay-equivalent by definition). Entries come
     back VERBATIM — the five actor shapes of پیوست ب are the door's own story and
     `lib/auditActor.ts` is what reads them — and `total` counts the filter over the whole ledger, not
     the page. `note` is on every payload, empty page included. */
  listAudit(query: ListAuditQuery, options?: ReadOptions): Promise<AuditListResponse>;

  /* ---- the signing keys this deployment publishes — `GET /ops/v1/keys` (door U-08-b, page U-16-e1) ----
     Capability `keys.read`, owner-only: no scope in `acs_service/identity.py::SCOPE_TO_CAPABILITY`
     maps to it, so a machine caller is refused (403) and among the four human roles only مالک passes —
     measured, not asserted, by `tests/test_acs_u16e_keys_door.py` and by `tools/lab_day.py`'s
     `role-walk` step (family `keys-door`). Read-only ⇒ no `Idempotency-Key` (a GET is
     replay-equivalent by definition). It publishes metadata only: no private half (AGENTS invariant ۴
     — nothing under `ACS_SECRET_DIR` is ever a response body) and no public `x` either, because
     `/.well-known/jwks.json` is the public half and this console page must not duplicate or proxy it.
     The source is `jwks.inventory()`, never `active_key()` — the latter would generate and write a key
     pair when the file is missing, and asking which key is live must never be the event that creates
     one. */
  listKeys(options?: ReadOptions): Promise<SigningKeyListResponse>;

  /* ---- operator profile — the six doors of `acs_service/routers/profile.py` (U-06, page U-16-e2) ----
     Read doors (`GET /operator-profile`, `POST …:validate`, `GET …/versions`, `GET …/readiness`) carry
     capability `profile.read`; the two activating doors (`PUT /operator-profile`,
     `POST …:rollback`) carry `profile.activate` AT LEVEL `edit` — the matrix is editable through
     `policy.matrix`, so a write door accepting a `read` level would be §۳-۳'s failure wearing a read
     name. `:validate` takes NO `Idempotency-Key` by the contract's own rule (replay-equivalence, not
     «every write»): it has no effect, and a stored replay would glue the FIRST verdict onto a document
     the operator edited in between (`routers/profile.py` says so at length). The PUT and the rollback
     mint a version per success, so their replay is a second fact and the key is required there —
     `assertWriteKey` keeps the console on the required side of that line. */

  /** `GET /ops/v1/operator-profile` — the active document verbatim, and the only export format. */
  getOperatorProfile(options?: ReadOptions): Promise<ProfileReadResponse>;

  /**
   * `POST /ops/v1/operator-profile:validate` — judge a candidate without storing a byte.
   * The report carries EVERY complaint in one pass (§۲ step ۲); the body travels as the open
   * document, because a second client-side schema would answer the first error instead.
   */
  validateOperatorProfile(document: ProfileDocument): Promise<ProfileValidationReport>;

  /** `PUT /ops/v1/operator-profile` — mint an immutable version (compare-and-swap on `expected_version`). */
  activateOperatorProfile(
    body: ProfileActivationRequest,
    options: WriteOptions,
  ): Promise<ProfileActivationAccepted>;

  /** `GET /ops/v1/operator-profile/versions` — the bounded history window, newest first, no documents. */
  listOperatorProfileVersions(options?: ReadOptions): Promise<ProfileVersionListResponse>;

  /** `POST /ops/v1/operator-profile:rollback` — bring a stored version's content forward as a NEW version. */
  rollbackOperatorProfile(
    body: ProfileRollbackRequest,
    options: WriteOptions,
  ): Promise<ProfileActivationAccepted>;

  /**
   * `GET /ops/v1/operator-profile/readiness` — the report that reports and decides nothing: it never
   * authorizes a write (every fleet row carries `write_authorized: false` by design), and the console
   * must treat its `release_blocked` as the door's own answer, not as a permission either way.
   */
  getOperatorProfileReadiness(options?: ReadOptions): Promise<ProfileReadinessResponse>;

  listOlts(options?: ReadOptions): Promise<OltListResponse>;
  listOltOnus(
    oltId: string,
    query: ListOltOnusQuery,
    options?: ReadOptions,
  ): Promise<OnuTableResponse>;

  /**
   * `GET /ops/v1/olts/{olt_id}/pon-capacity` (V7, G-PRC-08) — the OLT's ports measured
   * against the capacity the operator DECLARED (`settings:pon_capacity_onus_per_port`).
   * `ports` is null when nobody read the raw table, and the panel must render that as
   * «unknown», never as an empty list.
   */
  getOltPonCapacity(oltId: string, options?: ReadOptions): Promise<PonCapacityResponse>;

  /**
   * `POST /ops/v1/olts/{olt_id}/onus:authorize` — the DIRECT manual authorize
   * (scope `activation:write`, `Idempotency-Key` REQUIRED). It bypasses the ZTP
   * activation job entirely, which is why it is the repair/retry path.
   */
  authorizeOnu(
    oltId: string,
    body: OnuAuthorizeRequest,
    options: WriteOptions,
  ): Promise<OnuAuthorizeResponse>;

  /**
   * `GET /ops/v1/devices/{acs_device_id}/optical`. When the device has no known
   * OLT association this is a **409 `stale-snapshot`** — a real state of the
   * system ("no OLT association known"), not a transport failure.
   */
  getDeviceOptical(
    acsDeviceId: string,
    query: { window?: OpticalWindow },
    options?: ReadOptions,
  ): Promise<OpticalRead>;

  /**
   * `GET /ops/v1/devices/{acs_device_id}/optical/history` (V3, G-TLM-02). The stored RX/TX
   * series over one window — read from what the optical sweep recorded, never from the plant.
   * Two consequences the console has to respect: an empty window is `points: []` (a real
   * answer, not a failure), and no OLT association is required, because a reading already
   * taken belongs to the device and not to whichever OLT happens to answer today.
   */
  getDeviceOpticalHistory(
    acsDeviceId: string,
    query: { window?: OpticalWindow },
    options?: ReadOptions,
  ): Promise<OpticalHistory>;

  /**
   * `GET /ops/v1/optical/worst` (V3b, G-TLM-03). The fleet's darkest RX readings over one
   * window, plus the histogram of the per-device minima. `devices`/`samples`/`histogram` count
   * the whole rolled-up fleet even when `limit` cuts the list, so a page can never report its own
   * size as the population; an unmeasured fleet is `devices: 0` with empty lists, not an error.
   */
  getOpticalWorst(
    query: { window?: OpticalWindow; limit?: number },
    options?: ReadOptions,
  ): Promise<OpticalWorst>;

  /**
   * `GET /ops/v1/optical/degrading` (V3c, G-TLM-03). Which ONUs are falling and how long until each
   * crosses the warn line, estimated half-over-half from the stored rows. Three things a caller has
   * to respect: only `7d` and `30d` are answered (a dBm/day rate from two hours is a guess), the
   * countdown is `null` for a device already past the line or beyond the horizon, and
   * `insufficient` says how many devices the door refused to judge — so an empty list is not the
   * same sentence as a healthy fleet.
   */
  getOpticalDegrading(
    query: { window?: OpticalDegradingWindow; limit?: number },
    options?: ReadOptions,
  ): Promise<OpticalDegrading>;

  /* ---- firmware registry + campaigns — root-absolute `/ops/v1` (circuit E) ----
     `GET /ops/v1/firmware` (scope ops:read) answers metadata only — the image bytes
     are in GenieACS file storage and no read surface returns them. Campaigns are the
     wave ladder: `GET /ops/v1/campaigns` is the list view, `GET …/{campaign_id}` the
     detail, and both carry numbers DERIVED at read time from the campaign's download
     jobs plus `next_action`, which is literally the decision the `acs-campaigns`
     worker is acting on (§۴.۱۳'s «why is my wave not moving?»). */
  listFirmware(query: ListFirmwareQuery, options?: ReadOptions): Promise<FirmwareListResponse>;
  listCampaigns(query: ListCampaignsQuery, options?: ReadOptions): Promise<CampaignListResponse>;
  getCampaign(campaignId: string, options?: ReadOptions): Promise<Campaign>;

  /* ---- firmware + campaign writes — root-absolute `/ops/v1` (circuit E) ----
     Two registration doors, one registry: `firmware:upload` sends the image bytes as the
     body (E1 / G-FW-01 — a 20-100 MB ONT image does not survive base64 in a document) and
     the JSON door stays at its 4 MiB decoded ceiling. Since E2 the declared
     `checksum_sha256` is MEASURED by the service over the bytes that arrived, so the
     console sends the vendor's claim and never a digest of its own — a self-computed
     checksum could only ever agree with the file it came from, which is the opposite of
     what the check is for. A disagreement is 422 `checksum-mismatch` naming both digests.
     Campaign writes are the ladder: the create SCHEDULES wave 0 (E4 / G-FW-06, dispatch is
     the worker's), and `:halt` / `:resume` / `:rollback` are the operator's doors. */
  uploadFirmwareImage(
    image: FirmwareImageUpload,
    options: WriteOptions,
  ): Promise<FirmwareUploadAccepted>;
  deleteFirmware(fileId: string, options: WriteOptions): Promise<DeletedResponse>;
  createCampaign(body: CampaignCreateRequest, options: WriteOptions): Promise<CampaignScheduled>;
  haltCampaign(campaignId: string, options: WriteOptions): Promise<CampaignHalted>;
  resumeCampaign(campaignId: string, options: WriteOptions): Promise<CampaignAccepted>;
  rollbackCampaign(
    campaignId: string,
    body: CampaignRollbackRequest,
    options: WriteOptions,
  ): Promise<CampaignRolledBack>;

  /* ---- fleet jobs — `GET /ops/v1/jobs` (`routers/system.py:177`, U-16-f) ----
     One door, two shapes: with `device` it is this device's history, without it the fleet list.
     `type` is an ACTION PREFIX (the door's own docstring: `diagnostics:` → the probe family),
     `total` counts the rows matching THIS filter over the whole set, and capability is `jobs.read`.
     The row is the lifecycle only — the claim about the device (`verdict`) is on the full job
     record, so no list row here can read as an applied write (invariant ۳ / D6). */
  listJobs(query: ListJobsQuery, options?: ReadOptions): Promise<JobsListResponse>;
  /** The same door with `device` pinned to the path entity — the device panel's read. */
  listDeviceJobs(
    acsDeviceId: string,
    query?: Omit<ListJobsQuery, 'device'>,
    options?: ReadOptions,
  ): Promise<JobsListResponse>;

  /* ---- per-device operations / bulk / workflow — `routers/operations.py` (U-16-f) ----
     The read doors carry `device.params.read`; the three write doors are on DIFFERENT keys:
     `device.params.set` for a slot write and an RPC (`:run`), `device.params.raw` for the raw-params
     door, which is `edit` for owner/admin and `none` for operator/user (`identity.py::_DEFAULT_LEVELS`),
     so it can never share a gate with the slot write. All three writes REQUIRE `Idempotency-Key` and
     answer **202 queued**: an acceptance, never a claim about the device (§۴.۳/§۴.۵/§۴.۱۰ and D6). */
  listDeviceOperations(
    acsDeviceId: string,
    query?: ListOperationsQuery,
    options?: ReadOptions,
  ): Promise<OperationsResponse>;
  getOperationValue(
    acsDeviceId: string,
    op: string,
    query?: { refresh?: boolean },
    options?: ReadOptions,
  ): Promise<OperationValueResponse>;
  setOperationValue(
    acsDeviceId: string,
    op: string,
    body: OperationWriteRequest,
    options: WriteOptions,
  ): Promise<OperationWriteAccepted>;
  runOperation(
    acsDeviceId: string,
    op: string,
    body: OperationRunRequest,
    options: WriteOptions,
  ): Promise<OperationRunAccepted>;
  writeRawParameters(
    acsDeviceId: string,
    body: RawWriteRequest,
    options: WriteOptions,
  ): Promise<OperationWriteAccepted>;

  /* ---- provisioning templates (presets) — `GET /ops/v1/templates`, `PUT /ops/v1/templates/{name}` (G-PRV-05) ----
     The read is a passthrough of the NBI's whole list (no cursor); the write requires
     `templates.read` AT EDIT (owner/admin) and an Idempotency-Key whose sketch carries the WHOLE
     document — the same key with a changed document is a 409 replay conflict, so an edit mints a
     fresh key. The answer echoes the document verbatim; there is deliberately no created/replaced. */
  listTemplates(options?: ReadOptions): Promise<TemplateListResponse>;
  upsertTemplate(
    name: string,
    document: Record<string, unknown>,
    options: WriteOptions,
  ): Promise<PresetWriteResponse>;

  /* ---- reconciliation — `GET /ops/v1/reconciliation` (read door; the C6 worker is not wired) ----
     The payload's own `note` says what an empty list means; the page must render it, never
     mistake it for «the fleet reconciles». */
  listReconciliation(options?: ReadOptions): Promise<ReconciliationListResponse>;

  /* ---- console aggregate the HTTP provider does not call (see file header) ---- */
  getDeviceCapabilities(acsDeviceId: string, options?: ReadOptions): Promise<DeviceCapabilities>;

  /* ---- human identity — `/ops/v1/sessions*`, `/ops/v1/permissions/effective` (R1, U-08-f) ----
     Three doors and no fourth: login authenticates itself so it takes NO `Idempotency-Key`
     (`docs/RBAC_AND_CONSOLE_FA.md` §۶ names it the exception — several concurrent sessions for one
     user is the request, not a side effect), logout is a write and therefore DOES take one, and the
     effective read answers whoever is signed in, whatever their role. `api/session.ts` is the only
     caller; pages ask it, never these methods, so the token never reaches a component. */
  openSession(body: SessionRequest): Promise<SessionGranted>;
  /** `POST /ops/v1/sessions/totp` — the second step of an enrolled account's login. Same reply
   *  shape as `openSession`: this time it carries the token. Wrong/expired/spent code and an
   *  unknown challenge are ONE 401 `signature-invalid`, so the page must not try to tell them
   *  apart; the `login:` and `totp:` budgets are separate server-side, so a wrong code cannot
   *  spend the password budget. */
  submitTotp(body: TotpSessionRequest): Promise<SessionGranted>;
  closeSession(options: WriteOptions): Promise<SessionRevoked>;
  effectivePermissions(): Promise<EffectivePermissions>;

  /* ---- console users + ops settings — `routers/identity.py` and `routers/system.py` (U-16-g1) ----
     Three different capability keys live behind these six doors, and the page gates each surface with
     its OWN one (`docs/RBAC_AND_CONSOLE_FA.md` §۹): the four user doors run on `user.manage`, the
     settings read on `settings.read` and the settings write on `settings.thresholds` **at edit**
     (`system.py:263` passes the level to `require_capability`). `§۴` publishes `user.manage` and
     `policy.matrix` owner-only and `settings.thresholds` to owner+admin, so an operator can read the
     thresholds and cannot write them — which is exactly why the two write keys are not one gate.
     The list is complete rather than paged because the door says so (`routers/identity.py::list_users`:
     tens of console users, not thousands), so there is no cursor here to invent. */

  /** `GET /ops/v1/users` — every console user, with no secret field (`models.py::ConsoleUser`). */
  listUsers(options?: ReadOptions): Promise<UserListResponse>;

  /**
   * `POST /ops/v1/users` — `Idempotency-Key` REQUIRED (`Header(...)` in the door, so a missing key is a
   * 422 on the wire). A created user IS a second row and a replayed body would be a second account, so
   * replay-equivalence demands the key here. A taken username is 409 `user-exists`.
   */
  createUser(body: UserCreateRequest, options: WriteOptions): Promise<UserMutationAccepted>;

  /**
   * `PATCH /ops/v1/users/{user_id}` — role, status or password, each field independent.
   *
   * The §۳-۲ last-owner guard is INSIDE this write (`store.put_user_guarded` evaluates
   * `identity.last_owner_allows` in the same critical section), so the client never prevents the attempt
   * and never learns the rule: a refused demotion or disable answers 409 `last-owner` with the door's own
   * sentence and leaves the row byte-identical. `status → disabled` and a password change take this user's
   * live sessions with them (U-02/AUD-02); a role change takes none.
   */
  patchUser(
    userId: string,
    body: UserPatchRequest,
    options: WriteOptions,
  ): Promise<UserMutationAccepted>;

  /** `DELETE /ops/v1/users/{user_id}` — the same §۳-۲ guard as a demotion, and the same refusal. */
  deleteUser(userId: string, options: WriteOptions): Promise<UserMutationAccepted>;

  /** `GET /ops/v1/settings` — the whitelist's effective values plus the intent note. */
  getOpsSettings(options?: ReadOptions): Promise<OpsSettingsRead>;

  /**
   * `PUT /ops/v1/settings` — merge, not replace; an omitted key keeps its stored value.
   *
   * The answer is the same payload shape as the read and carries `SETTINGS_INTENT_NOTE`: this records
   * operator INTENT and applies nothing live (the C5 worker is not wired), so the page must show what the
   * server said it intended rather than an «applied» sentence of its own (invariant ۳ / D6).
   */
  putOpsSettings(values: Record<string, number>, options: WriteOptions): Promise<OpsSettingsRead>;

  /* ---- permission matrix — `GET`/`PUT /ops/v1/permissions` (`routers/identity.py`, U-16-g2) ----
     Both doors stand on `policy.matrix` (§۱'s key, owner-only in §۴ and LOCKED there by §۳-۱). The read
     answers `models.py::PermissionMatrix`: capability → role → level, COMPLETE for every published role
     (§۳-۴ makes a silent cell `none`, never an absence), with the `roles` and `levels` vocabularies riding
     along so the editor holds no second copy of §۱/§۲. The write takes `{cells: [{capability, role,
     level}]}` and the header: `identity.validate_matrix_write` judges the whole batch against §۳ BEFORE
     the store sees a row — an unknown capability/role/level is 422 `reference-invalid`, moving
     `policy.matrix`/`user.manage` off the owner is 409 `policy-conflict`, and a resulting matrix with
     `edit` while holding `none` on the read sibling is 422 (§۳-۳). A refusal leaves the matrix
     byte-identical and writes NO audit line — the page renders those sentences, never a paraphrase and
     never a client-side prevention (§۹: آینهٔ پاسخ سرور). The accepted answer is
     `MatrixMutationAccepted`: every requested cell with its before/after (restated cells included — a
     no-op is allowed to say so), the matrix as it now stands, and the door's own count sentence. The
     write is per-ROLE only (§۱۲: no per-user override exists to offer), and it never moves what a machine
     client's scopes resolve to — the matrix is the human grid. */

  /** `GET /ops/v1/permissions` — the whole grid the server enforces, for the owner who edits it. */
  getPermissionMatrix(options?: ReadOptions): Promise<PermissionMatrixRead>;

  /**
   * `PUT /ops/v1/permissions` — one cell or a batch, judged together against §۳ by the door.
   * `Idempotency-Key` REQUIRED: the sketch carries the cells, so a double click replays the stored
   * answer (replay-equivalence, the repo's real rule) and a same-key different-body write is the door's
   * 409 `idempotency-conflict`, not a second change of law.
   */
  putPermissionMatrix(
    body: MatrixWriteBody,
    options: WriteOptions,
  ): Promise<MatrixMutationAccepted>;

  /* ---- system measurement — `components` · `queues` · `workers` (O4, DA-09; a page since TLM-4b) ----
     All three doors stand on `health.read` — the same key the health rollup uses, so no new
     capability was invented for them. The components door is the EVIDENCE view of the same four
     components (api / store / nbi / adapter) the health rollup names, with the store's detail also
     carrying its queue `depths`; the queues door is the depths themselves (the outbox by its four
     states, the jobs by the seven lifecycle words) plus the live console-session count; the workers
     door (DA-09) is the five — now six — loops that the rollup never knew about.

     THE TRUTHFULNESS RULE, and it is the reason these methods exist in this shape: a depth the
     server did not send is NOT a zero, and a worker it never heard from is NOT a row. The server
     sends the whole closed vocabulary when it measured, `null` when it could not measure at all,
     and nothing at all about a loop that has not beaten — and this layer keeps all three as they
     arrived. A console that shows «0» for a measurement the façade never made, or «down» for a
     worker it cannot know about, is lying; that is the one thing an ops surface may not do.

     THE PAGE IS `src/pages/SystemPage.tsx` (plan page ۱۴, group `monitoring`). For most of the
     console's life these three doors had types, methods, parsers, a fixture and a measurement —
     and no consumer except a shape test, which is how «the five loops came out of invisibility»
     was only true for someone holding curl. The dashboard keeps its own `/system/health` read:
     one summary of one fact, in one screen.

     `/metrics` has NO client method on purpose: it is the Prometheus text exposition, scraped by
     Prometheus, not consumed by a browser. A console method for it would be a JSON-shaped reading
     of a plain-text body, and the plain-text body is the contract. */

  /** `GET /ops/v1/system/components` — the four components and their evidence. */
  getSystemComponents(options?: ReadOptions): Promise<SystemComponentsResponse>;

  /** `GET /ops/v1/system/queues` — the queue depths and the live session count. */
  getSystemQueues(options?: ReadOptions): Promise<SystemQueuesResponse>;

  /**
   * `GET /ops/v1/system/workers` — which loops have beaten, and what the door made of that
   * (DA-09, given a screen by TLM-4b).
   *
   * The row's `status` is the SERVER's judgement and this layer keeps it as it arrived: the
   * same `judge_workers()` answers `/metrics`, so the exporter and the screen cannot disagree.
   * A worker with no row is absent from the map, and absent is neither `down` nor `ok` — the
   * façade cannot know whether the deployment runs it.
   */
  getSystemWorkers(options?: ReadOptions): Promise<SystemWorkersResponse>;

  /* ---- the subscriber surface (S1, G-SUB-01/03) ----
     Six doors on three capability keys, and every one of them is a HUMAN door:
     no machine scope maps to `subscriber.read`, `subscriber.manage` or
     `service.profile`, so the console's session and the role×matrix decide them.

     THE MASK IS THE SERVER'S. `GET /ops/v1/subscribers` and
     `GET /ops/v1/subscribers/{subscriber_id}` answer with the identifying fields
     (`mobile` · `national_id` · `contract_number`) MASKED for a caller holding
     `subscriber.read` at `read` and WHOLE for one holding it at `edit` — the
     level on the key decides the fields, and there is no request flag that
     could. So these methods take no `full` parameter: the console renders the
     answer it is given, and a masked value is a value, not a missing one.

     THE SERVICE PROFILE CARRIES NO MASK AT ALL: a profile is a commercial shape
     (a speed, a VLAN, a DBA), not a person, so every reader of `service.profile`
     sees the same fields.

     `PUT /ops/v1/service-profiles/{profile_id}` is a FULL REPLACEMENT and takes
     NO `Idempotency-Key` — the body IS the row, so a retry writes the same row
     with the same values. The other two writes REQUIRE one: a replay there
     would mint a second row for one person. */

  /** `GET /ops/v1/subscribers` — the page, and the number BEHIND it. */
  listSubscribers(query?: ListSubscribersQuery): Promise<SubscriberListResponse>;

  /** `GET /ops/v1/subscribers/{subscriber_id}` — one row, masked or whole by the caller's level. */
  getSubscriber(subscriberId: string): Promise<Subscriber>;

  /** `POST /ops/v1/subscribers` — the door MINTS the identity; the body never names it. */
  createSubscriber(body: SubscriberCreateRequest, options: WriteOptions): Promise<Subscriber>;

  /** `GET /ops/v1/service-profiles` — the page, ordered by name. */
  listServiceProfiles(query?: ListServiceProfilesQuery): Promise<ServiceProfileListResponse>;

  /** `POST /ops/v1/service-profiles` — the door MINTS the key; a taken name is 409. */
  createServiceProfile(
    body: ServiceProfileWriteRequest,
    options: WriteOptions,
  ): Promise<ServiceProfile>;

  /** `PUT /ops/v1/service-profiles/{profile_id}` — a full replacement, and it takes NO key. */
  putServiceProfile(profileId: string, body: ServiceProfileWriteRequest): Promise<ServiceProfile>;

  /* ---- the subscriber search (S2, G-SUB-08) + the 360-degree view (S3, G-PRC-14) ----
     Two doors, and BOTH of them are reads — so neither of them carries an
     `Idempotency-Key`:

     * `POST /integration/v1/devices:search` is a POST because the criteria travel in
       the BODY: a phone number in a query string is a phone number in the access
       log, in the proxy's log and in the browser's history. The house rule is
       replay-EQUIVALENCE, not «every write takes a key» — a search has no effect
       on the caller's state, a replay of the same criteria yields the same page,
       and the published door declares NO key parameter. So this method takes no
       `WriteOptions` and the client sends no header.
     * `GET /ops/v1/subscribers/{subscriber_id}/overview` is the 360-degree view: six
       sections, each naming its own source, and a section whose row does not exist
       is `null` — absent, never invented.

     THE MASK IS STILL THE SERVER'S: the search door's own key is
     `fleet.devices.read` and the overview door's is `subscriber.read`, and the LEVEL
     on each key decides the identifying fields. The console sends no flag that
     could change it, and the parsers never mask again. */

  /**
   * `POST /integration/v1/devices:search` — the subscriber rows that match the
   * criteria, a page, and the number BEHIND the page.
   *
   * The criteria are a CONJUNCTION and at least one of them must be given: an empty
   * criteria is 422 `reference-invalid` and never becomes an unfiltered read.
   * `subscriber_ref` names the subscriber and matches EITHER of its two identities;
   * `serial` is a field of the DEVICE row and the answer is the subscriber who HOLDS
   * that device.
   */
  searchSubscribers(body: SubscriberSearchRequest): Promise<SubscriberSearchResponse>;

  /**
   * `GET /ops/v1/subscribers/{subscriber_id}/overview` — the 360-degree view of one
   * subscriber: the subscriber row, the device row, the binding row, the service
   * profile, and two BOUNDED pages (the device's alarms and the device's jobs).
   */
  getSubscriberOverview(subscriberId: string): Promise<SubscriberOverview>;

  /* ---- the subscriber's lifecycle (S4, G-SUB-05) ----
     Two doors, ONE capability key (`subscriber.manage` at `edit`) and **NO BODY AT
     ALL**: there is no field through which the caller could put personal data into the
     request, so no value of the subscriber's can reach the console's own request, the
     server's audit row, a log record, a metric label, an error detail or a problem
     body.

     A SUSPENSION IS ONE FIELD ON ONE ROW. The row's `status` moves and nothing else
     does — no second row, and no device row, no binding row and no profile row — so
     the resume undoes exactly what the suspension did. The suspended subscriber STILL
     EXISTS: it still appears in the search and in the 360-degree view, and the reader
     tells by the `status` field on the hit, never by an absent field.

     These doors do NOT claim that the service was cut: they record the operator's
     decision, and the network-side half of a real cut (the brand template and the
     RADIUS/CoA path) is not built. */

  /**
   * `POST /ops/v1/subscribers/{subscriber_id}:suspend` — the row's `status` goes
   * `active` → `suspended`. The `Idempotency-Key` is REQUIRED (a replay here is not
   * equivalent without it: the second call would meet a row that is already suspended
   * and answer its own conflict instead of the stored answer).
   */
  suspendSubscriber(subscriberId: string, options: WriteOptions): Promise<Subscriber>;

  /**
   * `POST /ops/v1/subscribers/{subscriber_id}:resume` — the row's `status` goes
   * `suspended` → `active`, and nothing else moves.
   */
  resumeSubscriber(subscriberId: string, options: WriteOptions): Promise<Subscriber>;
}

export type {
  ActionRequest,
  ActionResponse,
  ActivationAccepted,
  ActivationRequest,
  ActivationStatus,
  AlarmAckRequest,
  AlarmAckResponse,
  BindingRequest,
  BindingResponse,
  DiagnosticsRequest,
  OnuAuthorizeRequest,
  OnuAuthorizeResponse,
  SystemComponentsResponse,
  SystemQueuesResponse,
  SystemWorkersResponse,
};
